import React, { useCallback } from 'react'
import { ErrorMessage } from '@hookform/error-message'
import clsx from 'clsx'
import { useFormContext, useWatch } from 'react-hook-form'
import { Box, Typography } from '@mui/material'
import { Badge } from 'src/components/atoms'
import TextField from 'src/components/molecules/TextField'
import { FormValidation } from 'src/utils/form'
import { fullToHalf, fullHyphenToHalf } from 'src/utils/string'
import { TenantDetailFormNameEnum } from '../../type'
import styles from './styles.module.scss'

export type LabeledTextFieldWithCountProps = {
  label: string
  name: string
  type: string
  defaultValue?: string
  placeholder?: string
  required?: boolean
  validations?: FormValidation[]
  maxLength?: number
} & React.StyledProps

const LabeledTextFieldWithCount: React.FC<LabeledTextFieldWithCountProps> = ({
  className,
  label,
  name,
  type,
  defaultValue,
  placeholder,
  required,
  validations,
  maxLength,
}) => {
  const { trigger, formState, setValue } = useFormContext()
  const watchedValue = useWatch({
    name,
    defaultValue: defaultValue ?? '',
  })

  const handleBlur = useCallback(() => {
    if (name === TenantDetailFormNameEnum.staffTel) {
      const convertedValue = fullHyphenToHalf(fullToHalf(watchedValue))
      setValue(name, convertedValue)
    }
    trigger(name)
  }, [name, setValue, trigger, watchedValue])

  return (
    <Box className={clsx(className, styles.root)}>
      <Box className={styles.container}>
        <Typography component="h3" className={styles.label}>
          {label}
        </Typography>
        {required && (
          <Box>
            <Badge
              className={styles.badge}
              label="必須"
              size="small"
              color="red"
              fillType="color"
              ellipse
            />
          </Box>
        )}
      </Box>
      <Box>
        <Box className={styles.counterContainer}>
          {maxLength && (
            <Typography className={styles.counter}>
              {`${watchedValue.length}/${maxLength}`}
              <span>文字</span>
            </Typography>
          )}
        </Box>
        <TextField
          className={clsx(
            styles.textField,
            formState.errors[name] && styles.error
          )}
          name={name}
          type={type}
          defaultValue={defaultValue}
          placeholder={placeholder}
          required={required}
          validations={validations}
          maxLength={maxLength}
          onBlur={handleBlur}
        />
      </Box>
      <Box className={styles.errorContainer}>
        <ErrorMessage
          errors={formState.errors}
          name={name}
          render={({ message }) => {
            return (
              <Typography component="p" className={styles.errorMessage}>
                {message}
              </Typography>
            )
          }}
        />
      </Box>
    </Box>
  )
}

export default LabeledTextFieldWithCount
